import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CaretRight, MagnifyingGlass, TrendUp, TrendDown } from '@phosphor-icons/react';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { isNative } from '../lib/platform';
import { useIsPhone } from '../lib/useIsPhone';
import HoverCard from './HoverCard';
import ScoreBreakdown from './ScoreBreakdown';

// Shared building blocks for the app's look, sized up on desktop with md: classes.

const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Same bands as ScoreBadge, with a readable text tone for tinted backgrounds
export function scoreBand(score) {
  if (score >= 800) return { label: 'Excellent', color: '#1E6C30', text: '#1E6C30' };
  if (score >= 740) return { label: 'Very good', color: '#4d7c0f', text: '#3f6a0b' };
  if (score >= 670) return { label: 'Good', color: '#a3a323', text: '#6e6e12' };
  if (score >= 580) return { label: 'Fair', color: '#DCA114', text: '#9a6c06' };
  return { label: 'Poor', color: '#B0200C', text: '#B0200C' };
}

export function tap() {
  if (isNative) Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
}

export function LargeTitle({ eyebrow, title, subtitle, back }) {
  return (
    <div className="px-1 pt-2 pb-5 md:pt-0 md:pb-7">
      {back && (
        <Link to={back.to} onClick={tap} className="inline-flex items-center text-[15px] font-medium text-primary mb-2 -ml-0.5">
          <CaretRight size={16} weight="bold" className="rotate-180 mr-0.5" />
          {back.label}
        </Link>
      )}
      {eyebrow && <p className="text-[13px] font-medium text-gray-500 mb-0.5">{eyebrow}</p>}
      <h1 className="text-[32px] leading-[38px] md:text-[40px] md:leading-[46px] font-bold tracking-[-0.02em] text-[#111]">{title}</h1>
      {subtitle && <p className="text-[15px] text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
}

export function Section({ title, detail, children, className = '' }) {
  return (
    <section className={`mt-7 md:mt-10 ${className}`}>
      {(title || detail) && (
        <div className="flex items-baseline justify-between px-1 mb-2.5">
          <h2 className="text-[20px] font-bold tracking-[-0.01em] text-[#111]">{title}</h2>
          {detail && <span className="text-[13px] font-medium text-gray-500">{detail}</span>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Card({ children, className = '' }) {
  return (
    <div className={`bg-white rounded-[22px] shadow-[0_1px_2px_rgba(17,12,34,0.04),0_8px_24px_rgba(17,12,34,0.06)] ${className}`}>
      {children}
    </div>
  );
}

export function ListGroup({ children }) {
  return <Card className="overflow-hidden divide-y divide-[#F0EEF5]">{children}</Card>;
}

export function ListRow({ to, leading, title, subtitle, trailing, muted = false }) {
  const body = (
    <div className={`flex items-center gap-3.5 px-4 min-h-[68px] md:min-h-[72px] md:px-5 py-3 active:bg-[#F6F4FA] md:hover:bg-[#FAF9FC] transition-colors ${muted ? 'opacity-55' : ''}`}>
      {leading}
      <div className="flex-1 min-w-0">
        <p className="text-[16px] font-semibold text-[#111] truncate">{title}</p>
        {subtitle && <p className="text-[13px] text-gray-500 truncate mt-0.5">{subtitle}</p>}
      </div>
      {trailing}
      {to && <CaretRight size={16} weight="bold" className="text-gray-300 flex-shrink-0 -mr-1" />}
    </div>
  );
  return to ? <Link to={to} onClick={tap} className="block">{body}</Link> : body;
}

export function Monogram({ name, score, src }) {
  if (src) {
    return <img src={src} alt="" className="w-11 h-11 rounded-[14px] object-cover flex-shrink-0" />;
  }
  const initials = (name || '?')
    .replace(/,.*$/, '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const tone = score ? scoreBand(score).color : '#6C18C9';
  return (
    <div
      className="w-11 h-11 rounded-[14px] flex items-center justify-center flex-shrink-0 text-[15px] font-bold"
      style={{ backgroundColor: `${tone}1A`, color: tone }}
    >
      {initials}
    </div>
  );
}

export function ScorePill({ score, flagged = false, breakdown, weights, title }) {
  const isPhone = useIsPhone();
  if (score == null) return <span className="text-[13px] text-gray-400">No score</span>;
  const pill = <ScorePillBody score={score} flagged={flagged} />;
  // Desktop keeps the hover breakdown the old score badges had
  if (!isPhone && breakdown) {
    return (
      <HoverCard panel={<ScoreBreakdown breakdown={breakdown} score={score} weights={weights} title={title} />}>
        {pill}
      </HoverCard>
    );
  }
  return pill;
}

function ScorePillBody({ score, flagged }) {
  const band = scoreBand(score);
  return (
    <div className="flex flex-col items-end flex-shrink-0">
      <span className="flex items-center gap-1.5">
        {flagged && <span className="w-1.5 h-1.5 rounded-full bg-[#E5484D]" aria-label="Red zone" />}
        <span className="text-[19px] font-bold tabular-nums tracking-[-0.01em]" style={{ color: band.text }}>
          {Math.round(score)}
        </span>
      </span>
      <span className="text-[11px] font-semibold uppercase tracking-[0.04em]" style={{ color: band.text, opacity: 0.75 }}>
        {flagged ? 'Watch' : band.label}
      </span>
    </div>
  );
}

export function SearchField({ value, onChange, placeholder = 'Search' }) {
  return (
    <label className="flex items-center gap-2 h-11 px-3.5 rounded-[14px] bg-[#EDEBF3] text-gray-500 mb-3 lg:max-w-sm">
      <MagnifyingGlass size={18} weight="bold" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 bg-transparent text-[16px] text-[#111] placeholder-gray-500 outline-none"
      />
    </label>
  );
}

const BAND_KEY = [
  { label: 'Excellent', range: '800 to 850', color: '#1E6C30' },
  { label: 'Very good', range: '740 to 799', color: '#4d7c0f' },
  { label: 'Good', range: '670 to 739', color: '#a3a323' },
  { label: 'Fair', range: '580 to 669', color: '#DCA114' },
  { label: 'Poor', range: 'Below 580', color: '#B0200C' },
];

const CHART_SIDE = 12; // chart margin left and right, in px

// The headline card: a score, its band, change since the month before, and the trend.
export function ScoreHero({ label = 'Average CScore', data, focusMonth, focusYear }) {
  const [activeIdx, setActiveIdx] = useState(null); // point being touched or hovered
  const [showKey, setShowKey] = useState(false);
  const clearTimer = useRef(null);
  const chartRef = useRef(null);
  if (!data || data.length === 0) return null;

  const points = data.map((d) => ({ ...d, key: `${MONTHS[d.month]} ${String(d.year).slice(2)}` }));
  const latest = points[points.length - 1];
  // Show the month being touched, else the month picked below the card, else the latest
  const focusedIdx = points.findIndex((p) => p.month === focusMonth && p.year === focusYear);
  const shownIdx = activeIdx != null && points[activeIdx] ? activeIdx : focusedIdx >= 0 ? focusedIdx : points.length - 1;
  const shown = points[shownIdx];
  const prev = shownIdx > 0 ? points[shownIdx - 1] : null;
  const delta = prev ? Math.round(shown.score) - Math.round(prev.score) : null;
  const band = scoreBand(shown.score);
  const scores = points.map((p) => p.score);
  const pad = Math.max(8, (Math.max(...scores) - Math.min(...scores)) * 0.25);
  // Best and worst months get a dot and their score
  const hiIdx = scores.lastIndexOf(Math.max(...scores));
  const loIdx = scores.lastIndexOf(Math.min(...scores));

  // Touch: pick the nearest month to the finger. Recharts' own touch tracking
  // doesn't report the index back, so the header couldn't follow a drag.
  const pickFromTouch = (e) => {
    const el = chartRef.current;
    const t = e.touches?.[0];
    if (!el || !t || points.length < 2) return;
    const r = el.getBoundingClientRect();
    const x = t.clientX - r.left - CHART_SIDE;
    const i = Math.round((x / (r.width - CHART_SIDE * 2)) * (points.length - 1));
    clearTimeout(clearTimer.current);
    setActiveIdx(Math.min(points.length - 1, Math.max(0, i)));
  };
  const releaseSoon = () => {
    clearTimeout(clearTimer.current);
    clearTimer.current = setTimeout(() => setActiveIdx(null), 3000);
  };

  const dotEl = (cx, cy, r = 5) => <circle cx={cx} cy={cy} r={r} fill="#fff" stroke="#6C18C9" strokeWidth={2} />;

  const markDot = ({ cx, cy, index }) => {
    if (cx == null) return <g key={`d${index}`} />;

    // The touched / hovered month: a dark tag with its score and month
    if (index === activeIdx) {
      const w = 74;
      const h = 38;
      const width = chartRef.current?.offsetWidth || 9999;
      const left = Math.min(Math.max(cx - w / 2, 2), width - w - 2);
      const top = cy > 56 ? cy - h - 12 : cy + 12;
      const p = points[index];
      return (
        <g key={`d${index}`}>
          <line x1={cx} x2={cx} y1={8} y2={9999} stroke="rgba(255,255,255,0.35)" strokeWidth={1} />
          <rect x={left} y={top} width={w} height={h} rx={11} fill="rgba(24,4,52,0.6)" />
          <text x={left + w / 2} y={top + 17} textAnchor="middle" fill="#fff" fontSize={14} fontWeight={700}>
            {Math.round(p.score)}
          </text>
          <text x={left + w / 2} y={top + 31} textAnchor="middle" fill="rgba(255,255,255,0.75)" fontSize={11} fontWeight={600}>
            {MONTHS[p.month]} {p.year}
          </text>
          {dotEl(cx, cy, 6)}
        </g>
      );
    }

    // Best / worst month (or the only month)
    const single = points.length === 1;
    if (single || (hiIdx !== loIdx && (index === hiIdx || index === loIdx))) {
      const high = single || index === hiIdx;
      const anchor = single ? 'middle' : index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle';
      return (
        <g key={`d${index}`}>
          {dotEl(cx, cy)}
          <text x={cx} y={high ? cy - 12 : cy + 22} textAnchor={anchor} fill="#fff" fontSize={13} fontWeight={700} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(scores[index])}
          </text>
        </g>
      );
    }

    // The month picked below the card
    if (index === focusedIdx) return <g key={`d${index}`}>{dotEl(cx, cy)}</g>;
    return <g key={`d${index}`} />;
  };

  return (
    <div
      className="relative overflow-hidden rounded-[28px] text-white shadow-[0_12px_32px_rgba(108,24,201,0.35)]"
      style={{ background: 'radial-gradient(120% 90% at 0% 0%, #8B3FE6 0%, #6C18C9 45%, #3E0A78 100%)' }}
    >
      <div className="absolute -right-16 -top-20 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
      <button
        type="button"
        onClick={() => { tap(); setShowKey((v) => !v); }}
        className="absolute top-4 right-4 md:top-6 md:right-6 z-10 w-8 h-8 rounded-full bg-white/15 text-[14px] font-bold"
        aria-label="What the score bands mean"
        aria-expanded={showKey}
      >
        ?
      </button>
      <div className="relative px-5 pt-5 md:px-8 md:pt-7">
        <p className="text-[13px] font-medium text-white/70">{label}</p>
        <div className="flex items-end gap-3 mt-1">
          <span className="text-[56px] leading-[60px] md:text-[72px] md:leading-[76px] font-bold tabular-nums tracking-[-0.03em]">{Math.round(shown.score)}</span>
          <span className="mb-2.5 px-2.5 py-1 rounded-full bg-white/15 text-[12px] font-semibold backdrop-blur">
            {band.label}
          </span>
        </div>
        {/* Which month this score is for, then how it moved from the month before */}
        <p className="text-[15px] font-semibold text-white/90 mt-0.5">{MONTHS_LONG[shown.month]} {shown.year}</p>
        <div className="h-7 mt-2">
          {delta != null && (
            <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-white/15 text-[13px] text-white/85">
              {delta >= 0 ? <TrendUp size={14} weight="bold" /> : <TrendDown size={14} weight="bold" />}
              <span className="font-semibold text-white">{delta >= 0 ? '+' : ''}{delta}</span>
              from {MONTHS_LONG[prev.month]}
            </span>
          )}
        </div>
      </div>
      <div
        ref={chartRef}
        className="relative h-[160px] md:h-[230px] mt-1 md:mx-3"
        onTouchStart={pickFromTouch}
        onTouchMove={pickFromTouch}
        onTouchEnd={releaseSoon}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            accessibilityLayer={false}
            data={points}
            margin={{ top: 28, right: CHART_SIDE, left: CHART_SIDE, bottom: 24 }}
            onMouseMove={(state) => {
              // Recharts 3 passes the hovered point's index, not its data
              const i = Number(state?.activeTooltipIndex);
              if (Number.isInteger(i) && points[i]) {
                clearTimeout(clearTimer.current);
                setActiveIdx(i);
              }
            }}
            onMouseLeave={releaseSoon}
          >
            <defs>
              <linearGradient id="heroFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fff" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#fff" stopOpacity={0} />
              </linearGradient>
            </defs>
            <YAxis hide domain={[Math.min(...scores) - pad, Math.max(...scores) + pad]} />
            <XAxis dataKey="key" hide />
            <Tooltip content={() => null} cursor={false} />
            <Area
              type="monotone"
              dataKey="score"
              stroke="#fff"
              strokeWidth={2.5}
              fill="url(#heroFill)"
              dot={markDot}
              activeDot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="relative flex justify-between px-5 md:px-8 pb-4 md:pb-6 pt-1 text-[11px] md:text-[12px] font-medium text-white/60">
        <span>{points[0].key}</span>
        <span>{latest.key}</span>
      </div>

      {showKey && (
        <div className="absolute inset-0 z-20 bg-[#2A0758]/95 backdrop-blur px-5 py-5 md:px-8 md:py-7 flex flex-col" onClick={() => setShowKey(false)}>
          <p className="text-[17px] font-bold mb-1">CScore bands</p>
          <p className="text-[13px] text-white/70 mb-4">Scores run from 0 to 850.</p>
          <div className="space-y-2.5">
            {BAND_KEY.map((b) => (
              <div key={b.label} className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: b.color }} />
                <span className="text-[15px] font-semibold w-28">{b.label}</span>
                <span className="text-[15px] text-white/75 tabular-nums">{b.range}</span>
              </div>
            ))}
          </div>
          <p className="text-[12px] text-white/50 mt-auto">Tap anywhere to close</p>
        </div>
      )}
    </div>
  );
}

function Bone({ className = '' }) {
  return <div className={`bg-[#E9E6F0] rounded-xl animate-pulse ${className}`} />;
}

export function PageSkeleton({ hero = true, rows = 5 }) {
  return (
    <div>
      <div className="px-1 pt-2 pb-5">
        <Bone className="h-3.5 w-24 mb-2" />
        <Bone className="h-8 w-48" />
      </div>
      {hero && <Bone className="h-[300px] w-full rounded-[28px]" />}
      <Bone className="h-6 w-28 mt-7 mb-3" />
      <Card className="p-4 space-y-5">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-3.5">
            <Bone className="w-11 h-11 rounded-[14px]" />
            <div className="flex-1"><Bone className="h-4 w-2/3 mb-2" /><Bone className="h-3 w-1/3" /></div>
            <Bone className="h-6 w-10" />
          </div>
        ))}
      </Card>
    </div>
  );
}

export function Segmented({ options, value, onChange }) {
  return (
    <div className="flex p-1 rounded-[14px] bg-[#EDEBF3]">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => { tap(); onChange(o.value); }}
          className={`flex-1 h-9 rounded-[10px] text-[14px] font-semibold transition-all ${
            value === o.value ? 'bg-white text-[#111] shadow-[0_1px_3px_rgba(17,12,34,0.12)]' : 'text-gray-500'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function EmptyRow({ children }) {
  return <p className="px-4 py-6 text-center text-[15px] text-gray-500">{children}</p>;
}

const SHORT = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Horizontal month picker, newest first
export function MonthChips({ months, month, year, onSelect, allLabel }) {
  const chip = (on) => `h-10 px-4 rounded-full text-[14px] font-semibold whitespace-nowrap transition-colors ${
    on ? 'bg-primary text-white shadow-[0_4px_12px_rgba(108,24,201,0.3)]' : 'bg-white text-gray-700 shadow-[0_1px_2px_rgba(17,12,34,0.06)]'
  }`;
  return (
    <div className="-mx-4 px-4 md:mx-0 md:px-0 overflow-x-auto no-scrollbar">
      <div className="flex gap-2 w-max md:w-auto md:flex-wrap pb-1">
        {allLabel && (
          <button type="button" onClick={() => { tap(); onSelect(null, null); }} className={chip(!month)}>
            {allLabel}
          </button>
        )}
        {[...months].reverse().map((m) => {
          const on = m.month === month && m.year === year;
          return (
            <button
              key={`${m.year}-${m.month}`}
              type="button"
              onClick={() => { tap(); onSelect(m.month, m.year); }}
              className={`h-10 px-4 rounded-full text-[14px] font-semibold whitespace-nowrap transition-colors ${
                on ? 'bg-primary text-white shadow-[0_4px_12px_rgba(108,24,201,0.3)]' : 'bg-white text-gray-700 shadow-[0_1px_2px_rgba(17,12,34,0.06)]'
              }`}
            >
              {SHORT[m.month]} {String(m.year).slice(2)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function MetricTile({ label, value, hint }) {
  return (
    <Card className="p-4">
      <p className="text-[13px] font-medium text-gray-500">{label}</p>
      <p className="text-[28px] leading-[34px] font-bold tabular-nums tracking-[-0.02em] text-[#111] mt-1">{value}</p>
      {hint && <p className="text-[12px] text-gray-400 mt-0.5">{hint}</p>}
    </Card>
  );
}

// iOS-style switch
export function Switch({ on, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => { tap(); onChange(); }}
      className={`relative w-[51px] h-[31px] rounded-full flex-shrink-0 transition-colors ${on ? 'bg-[#34C759]' : 'bg-[#E3E1EA]'}`}
    >
      <span
        className={`absolute top-[2px] left-[2px] w-[27px] h-[27px] rounded-full bg-white shadow-[0_2px_4px_rgba(0,0,0,0.2)] transition-transform ${on ? 'translate-x-[20px]' : ''}`}
      />
    </button>
  );
}
